import { Link, Outlet, useNavigate } from 'react-router-dom';
import RouteTitle from './RouteTitle';
import { useAuth } from '../context/AuthContext';
import { BookOpen, LogOut, Home, LayoutDashboard, User, NotebookPen } from 'lucide-react';

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  function handleLogout() {
    logout();
    navigate('/');
  }

  return (
    <div className="min-h-screen bg-background">
      <RouteTitle />
      <header className="border-b border-gray-800 bg-background-secondary/50 backdrop-blur-sm sticky top-0 z-50" role="banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 text-white hover:text-accent-blue transition-colors" aria-label="Northcoast AI Academy home">
              <BookOpen className="w-6 h-6" aria-hidden="true" />
              <span className="font-semibold text-lg hidden sm:inline">Northcoast AI Academy</span>
              <span className="font-semibold text-lg sm:hidden">AI Academy</span>
            </Link>

            {/* Navigation */}
            <nav className="flex items-center gap-4" aria-label="Main navigation">
              <Link
                to="/"
                className="flex items-center gap-1.5 text-gray-400 hover:text-white transition-colors text-sm"
                aria-label="Home"
              >
                <Home className="w-4 h-4" aria-hidden="true" />
                <span className="hidden sm:inline">Home</span>
              </Link>

              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    className="flex items-center gap-1.5 text-gray-400 hover:text-white transition-colors text-sm"
                    aria-label="Go to your dashboard"
                  >
                    <LayoutDashboard className="w-4 h-4" aria-hidden="true" />
                    <span className="hidden sm:inline">Dashboard</span>
                  </Link>

                  <Link
                    to="/journal"
                    className="flex items-center gap-1.5 text-gray-400 hover:text-white transition-colors text-sm"
                    aria-label="View your thinking journal"
                  >
                    <NotebookPen className="w-4 h-4" aria-hidden="true" />
                    <span className="hidden sm:inline">Journal</span>
                  </Link>

                  <div className="flex items-center gap-3 ml-2 pl-4 border-l border-gray-700">
                    <Link
                      to="/profile"
                      className="flex items-center gap-1.5 text-gray-400 hover:text-white transition-colors text-sm"
                      aria-label="View your profile"
                    >
                      <User className="w-4 h-4" aria-hidden="true" />
                      <span className="hidden md:inline">{user.name || user.email}</span>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-1.5 text-gray-400 hover:text-red-400 transition-colors text-sm"
                      aria-label="Log out of your account"
                    >
                      <LogOut className="w-4 h-4" aria-hidden="true" />
                      <span className="hidden sm:inline">Logout</span>
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <Link
                    to="/login"
                    className="text-gray-400 hover:text-white transition-colors text-sm px-3 py-1.5"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="bg-accent-blue text-slate-900 text-sm px-4 py-1.5 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    Get Started
                  </Link>
                </div>
              )}
            </nav>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" role="main">
        <Outlet />
      </main>

      <footer className="border-t border-gray-800 py-8 mt-16" role="contentinfo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-4 text-sm text-gray-400">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p>Northcoast AI Academy &mdash; learn to think, build, and thrive with AI.</p>
            <p>
              From the team at{' '}
              <a href="https://northcoast.ai" target="_blank" rel="noopener noreferrer" className="text-accent-blue hover:underline">
                northcoast.ai
              </a>
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <nav aria-label="Footer" className="flex flex-wrap justify-center gap-x-4 gap-y-1">
              <Link to="/privacy" className="hover:text-white">Privacy</Link>
              <Link to="/terms" className="hover:text-white">Terms</Link>
              <Link to="/accessibility" className="hover:text-white">Accessibility</Link>
              <a href="mailto:info@northcoast.ai" className="hover:text-white">info@northcoast.ai</a>
            </nav>
            <p>&copy; 2026 Northcoast Intelligence, LLC</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
